import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/joy';
import type { RankingScanSummary } from '../../../types/marketing';

interface RankingTimelineProps {
  scans: RankingScanSummary[];
  selectedScanId: number | null;
  onSelect: (scanId: number) => void;
}

export const RankingTimeline: React.FC<RankingTimelineProps> = ({
  scans,
  selectedScanId,
  onSelect,
}) => {
  if (scans.length === 0) {
    return (
      <Typography level="body-sm" color="neutral" sx={{ py: 1 }}>
        No completed scans yet. Trigger a scan to see results.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        overflowX: 'auto',
        pb: 1,
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'neutral.300', borderRadius: 3 },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ minWidth: 'max-content' }}>
        {scans.map((scan) => {
          const isSelected = selectedScanId === scan.id;
          const date = scan.scanned_at ? new Date(scan.scanned_at) : null;

          return (
            <Chip
              key={scan.id}
              variant={isSelected ? 'solid' : 'soft'}
              color={isSelected ? 'primary' : 'neutral'}
              size="md"
              onClick={() => onSelect(scan.id)}
              sx={{
                cursor: 'pointer',
                flexDirection: 'column',
                alignItems: 'center',
                py: 0.5,
                minWidth: 90,
              }}
            >
              <Typography
                level="body-xs"
                fontWeight={isSelected ? 'lg' : 'md'}
                sx={{ color: isSelected ? 'inherit' : undefined }}
              >
                {date
                  ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  : 'Pending'}
              </Typography>
              {scan.ranking_score != null && (
                <Typography
                  level="body-xs"
                  fontWeight="lg"
                  sx={{
                    color: isSelected ? 'inherit' : getScoreColor(scan.ranking_score),
                  }}
                >
                  {scan.ranking_score.toFixed(1)}
                </Typography>
              )}
            </Chip>
          );
        })}
      </Stack>
    </Box>
  );
};

function getScoreColor(score: number): string {
  if (score >= 50) return '#4CAF50';
  if (score >= 25) return '#FF9800';
  return '#F44336';
}
